import dayjs from 'dayjs';

/**
 * Continuous recording with motion clips (camera-feeds plan, section 13).
 * The settings' defaults, limits and presets are twins of 3-API
 * models/camera/camera.model.ts (CAMERA_RECORDING_DEFAULTS,
 * CAMERA_RECORDING_LIMITS, CAMERA_STILL_INTERVALS_MIN,
 * CAMERA_SCENE_THRESHOLD_PRESETS).
 */
export const CAMERA_RECORDING_DEFAULTS = Object.freeze({
  enabled: false,
  quality: 'sub',
  motionSource: 'camera',
  sceneThreshold: 0.02,
  preRollSec: 30,
  postMotionSec: 300,
  maxClipMin: 30,
  retentionDays: 30,
  stillIntervalMin: 10,
});

/** What PATCH accepts for each numeric setting the form edits (inclusive). */
export const CAMERA_RECORDING_LIMITS = Object.freeze({
  preRollSec: { min: 0, max: 120 },
  postMotionSec: { min: 30, max: 1800 },
  maxClipMin: { min: 5, max: 60 },
  retentionDays: { min: 1, max: 365 },
});

export const CAMERA_STILL_INTERVALS_MIN = [5, 10, 15, 20, 30, 60];

/** The form's sensitivity presets for `sceneThreshold` (gateway motion). */
export const CAMERA_SCENE_THRESHOLD_PRESETS = Object.freeze({
  high: 0.008,
  normal: 0.02,
  low: 0.05,
});

/**
 * About how much SIM data continuous recording uses a month per camera, GB:
 * the H.264 sub stream at about 0.3 Mbit/s, the 4K H.265 main stream at
 * about 1.75 Mbit/s (plan section 13.1). `main` needs an unlimited plan.
 */
export const RECORDING_MONTHLY_DATA_GB = Object.freeze({ sub: 100, main: 570 });

/** A still every 10 minutes: at most this many a day. */
export const MAX_STILLS_PER_DAY = 144;

/** A camera's recording settings, the defaults filled in (older rows have none). */
export const getRecordingSettings = (camera) => ({
  ...CAMERA_RECORDING_DEFAULTS,
  ...((camera && camera.recording) || {}),
});

/** Whether the camera runs on mains power: only such a camera may record. */
export const isMainsPowered = (camera) =>
  Boolean(camera) && camera.powerSource === 'mains';

/**
 * Whether recording is on for the camera: mains-powered, enabled, recording
 * turned on. The gateway then keeps it connected all the time.
 */
export const isRecordingCamera = (camera) =>
  isMainsPowered(camera) &&
  camera.enabled !== false &&
  Boolean(camera.recording && camera.recording.enabled === true);

/**
 * Whether the camera may have recordings to show: it records, or it did
 * (3-API stamps `recording.enabledAt` when recording is turned on), or the
 * list counted clips today.
 */
export const mayHaveRecordings = (camera) =>
  Boolean(camera) &&
  (isRecordingCamera(camera) ||
    Boolean(camera.recording && camera.recording.enabledAt) ||
    camera.clipsToday > 0);

/** The preset closest to a `sceneThreshold`: 'high', 'normal' or 'low'. */
export const scenePresetOf = (threshold) => {
  const value = Number(threshold);
  if (!Number.isFinite(value)) return 'normal';
  return Object.keys(CAMERA_SCENE_THRESHOLD_PRESETS).reduce((best, key) =>
    Math.abs(CAMERA_SCENE_THRESHOLD_PRESETS[key] - value) <
    Math.abs(CAMERA_SCENE_THRESHOLD_PRESETS[best] - value)
      ? key
      : best
  );
};

/**
 * What the gateway's live status says of recording: 'motion' while a clip
 * is open ("Liikettä havaittu"), 'recording' while it records ("Tallentaa"),
 * null otherwise (the gateway sends `recording` only for a recording camera).
 * @param {Object} [status] the gateway's status of a view session
 * @returns {'motion'|'recording'|null}
 */
export const getLiveRecordingState = (status) => {
  const recording = status && status.recording;
  if (!recording || !recording.on) return null;
  return recording.clipOpenSince ? 'motion' : 'recording';
};

/** A day as the recordings are asked for: YYYY-MM-DD, local. */
export const toRecordingDay = (date) => dayjs(date).format('YYYY-MM-DD');

/** Today, as toRecordingDay. */
export const todayRecordingDay = () => toRecordingDay(new Date());

/** The day before or after (`offset` days) a YYYY-MM-DD day. */
export const shiftRecordingDay = (day, offset) =>
  toRecordingDay(dayjs(day).add(offset, 'day'));

/**
 * The instants of a local day for the clips route: `from` inclusive, `to`
 * exclusive, ISO.
 * @param {string} day YYYY-MM-DD
 */
export const recordingDayRange = (day) => {
  const start = dayjs(day).startOf('day');
  return {
    from: start.toISOString(),
    to: start.add(1, 'day').toISOString(),
  };
};

/**
 * A clip's length: "45 s" under a minute, "12 min" under an hour, else
 * "1 h 5 min".
 * @param {number} seconds
 * @param {Function} t
 */
export const formatClipDuration = (seconds, t) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) {
    return t('cameras.recordings.duration_seconds', '{{count}} s', {
      count: total,
    });
  }
  const minutes = Math.round(total / 60);
  if (minutes < 60) {
    return t('cameras.recordings.duration_minutes', '{{count}} min', {
      count: minutes,
    });
  }
  return t('cameras.recordings.duration_hours', '{{hours}} h {{minutes}} min', {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  });
};

/** A time of day the camera page's way: "12.03". */
export const formatRecordingTime = (iso, t) => {
  const time = dayjs(iso);
  return iso && time.isValid()
    ? time.format(t('cameras.time_format', 'HH.mm'))
    : '';
};

/** A recording day for headings: "28.10.2026". */
export const formatRecordingDay = (day) => dayjs(day).format('D.M.YYYY');
